import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { API_URL } from "../config";
import useToast from "../hooks/useToast";

const MIN_TOPUP_EUR = 15;

const TOPUP_PRESETS = [
  { amount: 15, tagKey: "min" },
  { amount: 25, tagKey: "starter" },
  { amount: 50, tagKey: "popular" },
  { amount: 100, tagKey: "best" },
  { amount: 200, tagKey: "pro" },
  { amount: 500, tagKey: "max" },
];

const TOPUP_TAGS = {
  min: { en: "Minimum", fr: "Minimum", ru: "Минимум" },
  starter: { en: "Starter", fr: "Débutant", ru: "Старт" },
  popular: { en: "Popular", fr: "Populaire", ru: "Популярный" },
  best: { en: "Best value", fr: "Le plus avantageux", ru: "Самый выгодный" },
  pro: { en: "For active use", fr: "Usage intensif", ru: "Для активных" },
  max: { en: "Maximum", fr: "Le plus grand", ru: "Самый большой" },
};

function topupTagLabel(tagKey, lang) {
  const row = TOPUP_TAGS[tagKey];
  if (!row) return "";
  if (lang === "fr") return row.fr;
  if (lang === "ru") return row.ru;
  return row.en;
}

export default function WalletModal({
  isOpen,
  onClose,
  token,
  lang,
  isDark,
  initialTab = "overview",
  onBalanceUpdated,
}) {
  const toast = useToast();
  const onBalanceUpdatedRef = useRef(onBalanceUpdated);
  onBalanceUpdatedRef.current = onBalanceUpdated;

  const [walletTab, setWalletTab] = useState("overview");
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupLoading, setTopupLoading] = useState(false);
  const [topupError, setTopupError] = useState("");

  const loadBalance = useCallback(async () => {
    if (!token) return;
    try {
      setBalanceLoading(true);
      const { data } = await axios.get(`${API_URL}/api/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const nextBalance = data.balance ?? data.amount ?? 0;
      setBalance(nextBalance);
      onBalanceUpdatedRef.current?.(nextBalance);
    } catch (err) {
      console.error("Error loading balance:", err);
      setBalance(0);
      onBalanceUpdatedRef.current?.(0);
    } finally {
      setBalanceLoading(false);
    }
  }, [token]);

  const loadTransactions = useCallback(async () => {
    if (!token) return;
    try {
      setTransactionsLoading(true);
      const { data } = await axios.get(`${API_URL}/api/wallet/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(
        Array.isArray(data.transactions)
          ? data.transactions
          : Array.isArray(data)
          ? data
          : []
      );
    } catch (err) {
      console.error("Error loading transactions:", err);
      setTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  }, [token]);

  const handleTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!amount || amount <= 0) {
      setTopupError(lang === "fr" ? "Montant invalide" : lang === "ru" ? "Неверная сумма" : "Invalid amount");
      return;
    }
    if (amount < MIN_TOPUP_EUR) {
      setTopupError(
        lang === "fr"
          ? `Montant minimum : €${MIN_TOPUP_EUR}`
          : lang === "ru"
          ? `Минимальная сумма: €${MIN_TOPUP_EUR}`
          : `Minimum amount is €${MIN_TOPUP_EUR}`
      );
      return;
    }
    try {
      setTopupLoading(true);
      setTopupError("");
      const { data } = await axios.post(
        `${API_URL}/api/wallet/topup`,
        {
          amount,
          // The backend whitelists the host against FRONTEND_URL and only
          // appends ?topup=success|cancel — sending the current page makes
          // the user land back exactly where they started the top-up.
          return_url: window.location.href.split("?")[0],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.payment_url) {
        window.location.assign(data.payment_url);
        return;
      }
      toast(lang === "fr" ? "Paiement initié !" : "Payment initiated!", "success");
      await loadBalance();
      await loadTransactions();
      setTopupAmount("");
      setTopupLoading(false);
    } catch (err) {
      console.error("Topup error:", err);
      setTopupError(
        err.response?.data?.detail ||
          (lang === "fr" ? "Erreur de paiement" : "Payment error")
      );
      setTopupLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setWalletTab(initialTab === "topup" ? "topup" : "overview");
    setTopupAmount("");
    setTopupError("");
    loadBalance();
    loadTransactions();
    // Only reset modal state when it opens — not when parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialTab]);

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 2000,
        animation: "fadeIn 0.2s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: isDark ? "#16213e" : "#ffffff",
          borderRadius: "24px 24px 0 0",
          width: "100%",
          maxWidth: 560,
          maxHeight: "88vh",
          overflowY: "auto",
          boxShadow: "0 -8px 40px rgba(0,0,0,0.35)",
          animation: "slideUp 0.3s cubic-bezier(0.175,0.885,0.32,1.1)",
        }}
      >
        {/* Handle bar */}
        <div style={{ display: "flex", justifyContent: "center", padding: "14px 0 4px" }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: isDark ? "#444" : "#ddd" }} />
        </div>

        {/* Header */}
        <div
          style={{
            padding: "12px 24px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${isDark ? "#ffffff15" : "#00000010"}`,
          }}
        >
          <div>
            <div style={{ fontSize: 13, color: isDark ? "#aaa" : "#888", marginBottom: 2 }}>
              {lang === "fr" ? "Mon portefeuille" : "My Wallet"}
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: isDark ? "#fff" : "#1a1a2e" }}>
              {balanceLoading
                ? "..."
                : balance !== null
                ? `€${Number(balance).toFixed(2)}`
                : "—"}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: isDark ? "#ffffff15" : "#00000010",
              border: "none",
              borderRadius: "50%",
              width: 36,
              height: 36,
              cursor: "pointer",
              fontSize: 18,
              color: isDark ? "#fff" : "#333",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: 4,
            padding: "12px 24px",
            borderBottom: `1px solid ${isDark ? "#ffffff10" : "#00000008"}`,
          }}
        >
          {[
            { id: "overview", label: lang === "fr" ? "Aperçu" : "Overview", icon: "💳" },
            { id: "topup", label: lang === "fr" ? "Recharger" : "Top Up", icon: "➕" },
            { id: "history", label: lang === "fr" ? "Historique" : "History", icon: "📋" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setWalletTab(tab.id);
                if (tab.id === "history") loadTransactions();
              }}
              style={{
                flex: 1,
                padding: "9px 4px",
                borderRadius: 10,
                border: "none",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 13,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                background:
                  walletTab === tab.id
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : isDark
                    ? "#ffffff0d"
                    : "#f0f0f5",
                color: walletTab === tab.id ? "#fff" : isDark ? "#ccc" : "#555",
                transition: "all 0.18s",
              }}
            >
              <span style={{ fontSize: 16 }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ padding: "20px 24px 36px" }}>

          {/* OVERVIEW TAB */}
          {walletTab === "overview" && (
            <div>
              <div style={{ fontSize: 14, color: isDark ? "#aaa" : "#777", marginBottom: 24, lineHeight: 1.5 }}>
                {lang === "fr"
                  ? "Gérez votre solde : rechargez votre compte ou consultez l'historique de vos transactions."
                  : "Manage your balance: top up your account or view your transaction history."}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {/* Top Up action */}
                <button
                  onClick={() => setWalletTab("topup")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "18px 20px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    border: "none",
                    borderRadius: 14,
                    cursor: "pointer",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 16,
                    textAlign: "left",
                    transition: "opacity 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  <span style={{ fontSize: 24, width: 32, textAlign: "center" }}>➕</span>
                  <span style={{ flex: 1 }}>
                    {lang === "fr" ? "Recharger le portefeuille" : "Top up wallet"}
                  </span>
                  <span style={{ fontSize: 20, opacity: 0.8 }}>›</span>
                </button>
                {/* History action */}
                <button
                  onClick={() => { setWalletTab("history"); loadTransactions(); }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "18px 20px",
                    background: isDark ? "#ffffff08" : "#f8f9ff",
                    border: `1px solid ${isDark ? "#ffffff15" : "#e0e0f0"}`,
                    borderRadius: 14,
                    cursor: "pointer",
                    color: isDark ? "#fff" : "#1a1a2e",
                    fontWeight: 600,
                    fontSize: 16,
                    textAlign: "left",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = isDark ? "#ffffff14" : "#eef0ff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = isDark ? "#ffffff08" : "#f8f9ff")
                  }
                >
                  <span style={{ fontSize: 24, width: 32, textAlign: "center" }}>📋</span>
                  <span style={{ flex: 1 }}>
                    {lang === "fr" ? "Voir l'historique" : "View history"}
                  </span>
                  <span style={{ fontSize: 20, color: "#667eea" }}>›</span>
                </button>
              </div>
            </div>
          )}

          {/* TOP UP TAB */}
          {walletTab === "topup" && (
            <div>
              <div
                style={{
                  fontSize: 14,
                  color: isDark ? "#aaa" : "#777",
                  marginBottom: 20,
                  lineHeight: 1.5,
                }}
              >
                {lang === "fr"
                  ? `Choisissez un montant (min. €${MIN_TOPUP_EUR}) ou entrez le vôtre.`
                  : lang === "ru"
                  ? `Выберите сумму (мин. €${MIN_TOPUP_EUR}) или введите свою.`
                  : `Choose an amount (min. €${MIN_TOPUP_EUR}) or enter a custom one.`}
              </div>

              {/* Preset amounts */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 10,
                  marginBottom: 20,
                }}
              >
                {TOPUP_PRESETS.map(({ amount: amt, tagKey }) => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setTopupAmount(String(amt))}
                    style={{
                      padding: "12px 8px 10px",
                      borderRadius: 12,
                      border:
                        topupAmount === String(amt)
                          ? "2px solid #667eea"
                          : `1px solid ${isDark ? "#ffffff20" : "#e0e0e8"}`,
                      background:
                        topupAmount === String(amt)
                          ? "linear-gradient(135deg, #667eea22 0%, #764ba222 100%)"
                          : isDark
                          ? "#ffffff08"
                          : "#f8f9ff",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      transition: "all 0.15s",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: 16,
                        color:
                          topupAmount === String(amt)
                            ? "#667eea"
                            : isDark
                            ? "#ddd"
                            : "#333",
                      }}
                    >
                      €{amt}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        textAlign: "center",
                        color:
                          topupAmount === String(amt)
                            ? "#667eea"
                            : isDark
                            ? "#888"
                            : "#888",
                        opacity: topupAmount === String(amt) ? 0.95 : 0.85,
                      }}
                    >
                      {topupTagLabel(tagKey, lang)}
                    </span>
                  </button>
                ))}
              </div>

              {/* Custom amount */}
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 13,
                    color: isDark ? "#aaa" : "#888",
                    marginBottom: 8,
                  }}
                >
                  {lang === "fr" ? "Montant personnalisé" : lang === "ru" ? "Своя сумма" : "Custom amount"}
                </label>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: 14,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: isDark ? "#aaa" : "#888",
                      fontWeight: 700,
                      fontSize: 18,
                    }}
                  >
                    €
                  </span>
                  <input
                    type="number"
                    min={MIN_TOPUP_EUR}
                    step="0.01"
                    value={topupAmount}
                    onChange={(e) => {
                      setTopupAmount(e.target.value);
                      setTopupError("");
                    }}
                    placeholder="0.00"
                    style={{
                      width: "100%",
                      padding: "14px 14px 14px 30px",
                      borderRadius: 12,
                      border: topupError
                        ? "1.5px solid #f24d4c"
                        : `1.5px solid ${isDark ? "#ffffff25" : "#e0e0e8"}`,
                      background: isDark ? "#0f0f1a" : "#fff",
                      color: isDark ? "#fff" : "#1a1a2e",
                      fontSize: 18,
                      fontWeight: 600,
                      boxSizing: "border-box",
                      outline: "none",
                    }}
                  />
                </div>
                {topupError && (
                  <div style={{ color: "#f24d4c", fontSize: 13, marginTop: 6 }}>
                    {topupError}
                  </div>
                )}
              </div>

              <button
                onClick={handleTopup}
                disabled={topupLoading || !topupAmount}
                style={{
                  width: "100%",
                  padding: "16px",
                  borderRadius: 14,
                  border: "none",
                  background: topupAmount
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : isDark
                    ? "#ffffff20"
                    : "#e0e0e8",
                  color: topupAmount ? "#fff" : isDark ? "#666" : "#aaa",
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: topupAmount ? "pointer" : "not-allowed",
                  opacity: topupLoading ? 0.7 : 1,
                  transition: "all 0.2s",
                }}
              >
                {topupLoading
                  ? lang === "fr"
                    ? "Traitement..."
                    : "Processing..."
                  : topupAmount
                  ? `${lang === "fr" ? "Recharger" : "Add"} €${Number(topupAmount || 0).toFixed(2)}`
                  : lang === "fr"
                  ? "Sélectionnez un montant"
                  : "Select an amount"}
              </button>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  marginTop: 14,
                  fontSize: 12,
                  color: isDark ? "#666" : "#bbb",
                }}
              >
                🔒{" "}
                {lang === "fr"
                  ? "Paiement sécurisé via Stripe"
                  : "Secured payment via Stripe"}
              </div>
            </div>
          )}

          {/* HISTORY TAB */}
          {walletTab === "history" && (
            <div>
              {transactionsLoading ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 0",
                    color: isDark ? "#aaa" : "#888",
                  }}
                >
                  {lang === "fr" ? "Chargement..." : "Loading..."}
                </div>
              ) : transactions.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 20px" }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                  <div style={{ color: isDark ? "#aaa" : "#888", fontSize: 14 }}>
                    {lang === "fr"
                      ? "Aucune transaction pour le moment"
                      : "No transactions yet"}
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {transactions.map((tx, i) => {
                    const isCredit = tx.type === "credit" || tx.amount > 0;
                    return (
                      <div
                        key={tx.id || i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 14,
                          padding: "14px 16px",
                          background: isDark ? "#ffffff07" : "#f8f9ff",
                          borderRadius: 12,
                          border: `1px solid ${isDark ? "#ffffff0d" : "#e8e8f0"}`,
                        }}
                      >
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            background: isCredit ? "#0abf3018" : "#f24d4c18",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 18,
                            flexShrink: 0,
                          }}
                        >
                          {isCredit ? "📥" : "📤"}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 14,
                              color: isDark ? "#fff" : "#1a1a2e",
                              marginBottom: 2,
                            }}
                          >
                            {tx.description ||
                              (isCredit
                                ? lang === "fr"
                                  ? "Rechargement"
                                  : "Top Up"
                                : lang === "fr"
                                ? "Paiement"
                                : "Payment")}
                          </div>
                          <div style={{ fontSize: 12, color: isDark ? "#888" : "#aaa" }}>
                            {tx.created_at
                              ? new Date(tx.created_at).toLocaleDateString(
                                  lang === "fr" ? "fr-FR" : "en-US",
                                  { day: "2-digit", month: "short", year: "numeric" }
                                )
                              : "—"}
                          </div>
                        </div>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 16,
                            color: isCredit ? "#0abf30" : "#f24d4c",
                            flexShrink: 0,
                          }}
                        >
                          {isCredit ? "+" : "−"}€{Math.abs(Number(tx.amount)).toFixed(2)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
